import {
  CalendarDetailsVisibility,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Sync16, Target, Trash2 } from 'shared/assets/icons';
import { FormSelect } from 'shared/components/ui/form-select';
import { Icon } from 'shared/components/ui/icon';
import { LinkedItemSearchMenu } from 'shared/components/ui/linked-item-search-menu';
import { ConnectionCalendar } from 'shared/hooks/queries/use-external-connections-calendars-queries';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { LinkOptions } from 'shared/types/task';

import { CalendarSelect, CalendarSelectValue } from './calendar-select';
import * as Styled from './calendar-sync-options.style';

export type CalendarSyncRowValue = {
  linkedItemId: ID | null;
  calendar: CalendarSelectValue | null;
  mode: SyncMode | null;
  detailsVisibility: CalendarDetailsVisibility | null;
};

export type CalendarSyncOptionRowProps = CalendarSyncRowValue & {
  goals: Goal[];
  lifeAreas: LifeArea[];
  calendars: ConnectionCalendar[];
  onChangeLinkedItem: (value: { id: ID; type: LinkedItemType } | null) => void;
  onChangeCalendar: (value: CalendarSelectValue) => void;
  onChangeMode: (mode: SyncMode) => void;
  onChangeVisibility: (visibility: CalendarDetailsVisibility) => void;
  onRemove: () => void;
};

export const CalendarSyncOptionRow: React.FC<CalendarSyncOptionRowProps> = ({
  linkedItemId,
  calendar,
  mode,
  detailsVisibility,
  goals,
  lifeAreas,
  calendars,
  onChangeLinkedItem,
  onChangeCalendar,
  onChangeMode,
  onChangeVisibility,
  onRemove,
}) => {
  const { t } = useTranslation();
  const linkedItemRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const linkedItem = useMemo(
    () =>
      goals.find(({ id }) => id === linkedItemId) ??
      lifeAreas.find(({ id }) => id === linkedItemId),
    [goals, lifeAreas, linkedItemId],
  );

  const onSelectLinkedItem = (link: { id: ID; type: LinkOptions } | null) => {
    onChangeLinkedItem(
      link
        ? {
            id: link.id,
            type:
              link.type === LinkOptions.Goal
                ? LinkedItemType.Goal
                : LinkedItemType.LifeArea,
          }
        : null,
    );
    closeMenu();
  };

  const modeOptions = [
    {
      value: SyncMode.Automatic,
      label: t(
        'forms.user-settings.sections.calendar.sync-tasks.mode.automatic',
      ),
    },
    {
      value: SyncMode.Manual,
      label: t('forms.user-settings.sections.calendar.sync-tasks.mode.manual'),
    },
  ];

  const visibilityOptions = [
    {
      value: CalendarDetailsVisibility.Visible,
      label: t(
        'forms.user-settings.sections.calendar.sync-tasks.visibility.visible',
      ),
    },
    {
      value: CalendarDetailsVisibility.Hidden,
      label: t(
        'forms.user-settings.sections.calendar.sync-tasks.visibility.hidden',
      ),
    },
  ];

  const placeholder = t(
    'forms.user-settings.sections.calendar.sync-tasks.placeholder',
  );

  return (
    <Styled.Row>
      <div ref={linkedItemRef}>
        <Styled.SelectButton type="button" onClick={openMenu}>
          <Styled.SelectValue>
            <Icon icon={Target} />
            {linkedItem ? (
              <Styled.SelectLabel>{linkedItem.name}</Styled.SelectLabel>
            ) : (
              <Styled.Placeholder>{placeholder}</Styled.Placeholder>
            )}
          </Styled.SelectValue>
          <Styled.ChevronContainer>
            <Icon icon={ChevronLeft} />
          </Styled.ChevronContainer>
        </Styled.SelectButton>

        {menuOpen && (
          <LinkedItemSearchMenu
            goals={goals}
            lifeAreas={lifeAreas}
            onChange={onSelectLinkedItem}
            value={linkedItem?.id}
            referenceElement={linkedItemRef}
            onOutside={closeMenu}
          />
        )}
      </div>

      <Styled.SwapIcon>
        <Icon icon={Sync16} />
      </Styled.SwapIcon>

      <CalendarSelect
        calendars={calendars}
        value={calendar}
        onChange={onChangeCalendar}
      />

      <FormSelect
        isSmall
        fullWidth
        placeholder={placeholder}
        options={modeOptions}
        value={mode ?? ''}
        onChange={(event) => onChangeMode(event.target.value as SyncMode)}
      />

      <FormSelect
        isSmall
        fullWidth
        placeholder={placeholder}
        options={visibilityOptions}
        value={detailsVisibility ?? ''}
        onChange={(event) =>
          onChangeVisibility(event.target.value as CalendarDetailsVisibility)
        }
      />

      <Styled.RemoveButton
        type="button"
        onClick={onRemove}
        aria-label={t(
          'forms.user-settings.sections.calendar.sync-tasks.remove',
        )}
      >
        <Icon icon={Trash2} />
      </Styled.RemoveButton>
    </Styled.Row>
  );
};
